import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Badge,
  Button,
  Modal,
  ListGroup
} from 'react-bootstrap';
import { 
  FaGraduationCap,
  FaCalendarAlt,
  FaFileAlt,
  FaCheckCircle,
  FaClock,
  FaSearch,
  FaListAlt,
  FaUserGraduate,
  FaCertificate,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaArrowRight,
  FaDownload,
  FaInfoCircle,
  FaQuestionCircle,
  FaRegCalendarCheck,
  FaBook,
  FaPenAlt,
  FaChalkboardTeacher,
  FaUniversity,
  FaAward,
  FaHandshake,
  FaChartLine,
  FaSync,
  FaRupeeSign,
  FaUsers,
  FaBookOpen,
  FaPaintBrush,
  FaHistory,
  FaUsersCog,
  FaBalanceScale,
  FaHome,
  FaLanguage,
  FaStethoscope
} from 'react-icons/fa';
import { BASE_URL } from '../../services/api';

// Course icons mapping
const COURSE_ICONS = {
  'arts': <FaPaintBrush />,
  'science': <FaBookOpen />,
  'commerce': <FaChartLine />,
  'engineering': <FaUniversity />,
  'medical': <FaStethoscope />,
  'law': <FaBalanceScale />,
  'management': <FaChalkboardTeacher />,
  'history': <FaHistory />,
  'sociology': <FaUsersCog />,
  'political science': <FaBalanceScale />,
  'economics': <FaChartLine />,
  'home science': <FaHome />,
  'kannada': <FaLanguage />,
  'hindi': <FaLanguage />,
  'english': <FaLanguage />,
  'default': <FaGraduationCap />
};

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;
  // Color schemes for different course types
  const courseColors = {
    'undergraduate': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'postgraduate': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'diploma': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'certificate': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'phd': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'default': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };

  // Course type filters
  const courseTypes = {
    'all': 'All Courses',
    'undergraduate': 'Undergraduate',
    'postgraduate': 'Postgraduate',
    'diploma': 'Diploma',
    'certificate': 'Certificate',
    'phd': 'PhD'
  };

  // Helper functions wrapped in useCallback
  const getShortDescription = useCallback((description) => {
    if (!description) return '';
    // Clean up the description by removing extra spaces and newlines
    const cleanDesc = description.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
    return cleanDesc.length > 100 ? cleanDesc.substring(0, 100) + '...' : cleanDesc;
  }, []);

  const getCourseType = useCallback((courseType) => {
    return courseType || 'undergraduate';
  }, []);

  const getCourseIcon = useCallback((courseName, description) => {
    const searchText = (courseName + ' ' + description).toLowerCase();
    
    // Check for specific subjects in course
    if (searchText.includes('arts') || searchText.includes('ba')) return COURSE_ICONS.arts;
    if (searchText.includes('science') || searchText.includes('bsc')) return COURSE_ICONS.science;
    if (searchText.includes('commerce') || searchText.includes('bcom')) return COURSE_ICONS.commerce;
    if (searchText.includes('kannada')) return COURSE_ICONS.kannada;
    if (searchText.includes('hindi')) return COURSE_ICONS.hindi;
    if (searchText.includes('english')) return COURSE_ICONS.english;
    if (searchText.includes('history')) return COURSE_ICONS.history;
    if (searchText.includes('sociology')) return COURSE_ICONS.sociology;
    if (searchText.includes('political')) return COURSE_ICONS['political science'];
    if (searchText.includes('economics')) return COURSE_ICONS.economics;
    if (searchText.includes('home science')) return COURSE_ICONS['home science'];
    
    return COURSE_ICONS.default;
  }, []);

  const getCourseColor = useCallback((courseType) => {
    return courseColors[courseType] || courseColors.default;
  }, []);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const extractSubjects = useCallback((description) => {
    if (!description) return [];
    const subjects = [];
    
    // Extract subjects from description
    const subjectKeywords = [
      'English', 'Kannada', 'Hindi', 'History', 'Sociology', 
      'Political Science', 'Economics', 'Home Science'
    ];
    
    subjectKeywords.forEach(subject => {
      if (description.includes(subject)) {
        subjects.push(subject);
      }
    });
    
    return subjects.slice(0, 4);
  }, []);

  const extractBasicSubjects = useCallback((description) => {
    if (!description) return '';
    const basicMatch = description.match(/Basic Subjects\s*:\s*([^\.]+)/i);
    return basicMatch ? basicMatch[1].trim() : '';
  }, []);

  const extractOptionalSubjects = useCallback((description) => {
    if (!description) return [];
    const optionalMatch = description.match(/Optional Subjects\s*:\s*([^\.]+)/i);
    if (optionalMatch) {
      return optionalMatch[1].split(',').map(subject => subject.trim()).slice(0, 5);
    }
    return [];
  }, []);

  // Fetch courses
  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${BASE_URL}/courses.php`);
      const result = await response.json();

      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        const enhancedCourses = result.data.map((course) => ({
          ...course,
          courseType: getCourseType(course.course_type),
          shortDescription: getShortDescription(course.description),
          basicSubjects: extractBasicSubjects(course.description),
          optionalSubjects: extractOptionalSubjects(course.description),
          subjectsList: extractSubjects(course.description),
          lastUpdated: new Date(course.updated_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }),
          isSeatAvailable: parseInt(course.intake_seats) > 0,
          seatStatus: parseInt(course.intake_seats) > 30 ? 'good' : 
                     parseInt(course.intake_seats) > 10 ? 'limited' : 
                     parseInt(course.intake_seats) > 0 ? 'few' : 'none'
        }));
        setCourses(enhancedCourses);
      } else {
        setError('No courses available');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [getCourseType, getShortDescription, extractBasicSubjects, extractOptionalSubjects, extractSubjects]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleViewDetails = (course) => {
    setSelectedCourse(course);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCourse(null);
    setShowModal(false);
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (course.description && course.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterType === 'all' || course.courseType === filterType;
    return matchesSearch && matchesFilter;
  });

  const availableCourseTypes = ['all', ...new Set(courses.map(c => c.courseType))];

  if (loading) {
    return (
      <Container className="courses-container py-5">
        <div className="text-center">
          <div className="loading-animation">
            <div className="loading-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
          <h4 className="mt-4 text-gradient">Loading Courses</h4>
          <p className="text-muted">Fetching course details...</p>
        </div>
      </Container>
    );
  }

  if (error || courses.length === 0) {
    return (
      <Container className="py-5">
        <Alert variant="warning" className="border-0 shadow-sm">
          <Alert.Heading>
            <FaInfoCircle className="me-2" />
            Courses Not Available
          </Alert.Heading>
          <p>{error || 'No courses found.'}</p>
          <Button variant="primary" onClick={fetchCourses}>
            <FaSync className="me-2" />
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="courses-portal">
      {/* Hero Section */}
      <div className="courses-hero">
        <Container className="py-5">
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <Badge bg="light" text="dark" className="hero-badge mb-3">
  <FaGraduationCap className="me-2" /> Academic Programs / Courses {currentYear}-{nextYear}
</Badge>
              <h1 className="display-5 fw-bold text-white mb-3">
                Explore Our <span className="text-gradient">Academic Programs / Courses</span>
              </h1>
              <p className="lead text-white-50 mb-4">
                Discover undergraduate, postgraduate, and professional programs 
                designed for your academic success and career growth.
              </p>
              <div className="hero-stats">
                <div className="stat-item">
                  <h3 className="text-white mb-0">{courses.length}+</h3>
                  <small className="text-white-50">Programs / Courses</small>
                </div>
                <div className="stat-item">
                  <h3 className="text-white mb-0">{courses.filter(c => c.isSeatAvailable).length}</h3>
                  <small className="text-white-50">Open for Admission</small>
                </div>
                <div className="stat-item">
                  <h3 className="text-white mb-0">100%</h3>
                  <small className="text-white-50">Quality Education</small>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-visual">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <div className="floating-shape shape-3"></div>
                <div className="courses-preview-card">
                  <div className="preview-icon">
                    <FaBookOpen />
                  </div>
                  <h5 className="mb-2">Diverse Programs / Courses</h5>
                  <p className="small mb-0">Wide range of academic choices</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        {/* Search and Filter Section */}
        <div className="search-filter-section mb-5">
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="search-wrapper">
                <div className="search-box">
                  <div className="search-icon">
                    <FaSearch />
                  </div>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search courses by name, code, or subjects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button 
                      variant="link" 
                      className="search-clear"
                      onClick={() => setSearchTerm('')}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="filter-wrapper">
                <div className="filter-badges">
                  {availableCourseTypes.map((type, index) => (
                    <Badge 
                      key={index}
                      bg={filterType === type ? 'primary' : 'light'} 
                      text={filterType === type ? 'white' : 'dark'}
                      className="filter-badge me-2 mb-2"
                      onClick={() => setFilterType(type)}
                      style={{ cursor: 'pointer' }}
                    >
                      {courseTypes[type] || type}
                    </Badge>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Courses Grid */}
        <Row className="g-4">
          {filteredCourses.map((course, index) => (
            <Col lg={4} md={6} key={course.id}>
              <div className="course-card-wrapper">
                <Card className="course-card border-0 shadow-lg">
                  <div 
                    className="course-card-header"
                    style={{ background: getCourseColor(course.courseType) }}
                  >
                    <div className="course-icon-wrapper">
                      <div className="course-icon">
                        {getCourseIcon(course.name, course.description)}
                      </div>
                      <Badge bg="light" text="dark" className="course-type-badge">
                        {courseTypes[course.courseType] || course.courseType}
                      </Badge>
                    </div>
                  </div>
                  <Card.Body className="p-4">
                    <h4 className="course-title mb-3">{course.name}</h4>
                    <div className="mb-3">
                      <code className="text-muted small bg-light p-1 rounded">{course.code}</code>
                    </div>
                    
                    <p className="course-description text-muted mb-3">
                      {course.shortDescription}
                    </p>
                    
                    <div className="course-subjects mb-3">
                      {course.subjectsList.length > 0 && (
                        <>
                          <h6 className="text-primary mb-2">
                            <FaBook className="me-2" />
                            Subjects
                          </h6>
                          <div className="d-flex flex-wrap gap-1">
                            {course.subjectsList.map((subject, idx) => (
                              <Badge key={idx} bg="light" text="dark" className="me-1 mb-1">
                                {subject}
                              </Badge>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="course-details mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted small">
                          <FaClock className="me-1" /> Duration
                        </span>
                        <span className="fw-bold">{course.duration}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted small">
                          <FaRupeeSign className="me-1" /> Fee
                        </span>
                        <span className="fw-bold text-success">{formatCurrency(course.fee)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                          <FaUsers className="me-1" /> Seats
                        </span>
                        <Badge 
                          bg={course.isSeatAvailable ? 
                            (course.seatStatus === 'good' ? 'success' : 
                             course.seatStatus === 'limited' ? 'warning' : 'danger') : 'secondary'}
                        >
                          {course.isSeatAvailable ? `${course.intake_seats} Seats` : 'Full'}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="course-stats">
                        <div className="stat">
                          <FaCalendarAlt className="stat-icon" />
                          <span className="stat-label">Updated: {course.lastUpdated}</span>
                        </div>
                      </div>
                      <Button
                        variant="primary"
                        className="course-action-btn"
                        onClick={() => handleViewDetails(course)}
                      >
                        View Details
                        <FaArrowRight className="ms-2" />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          ))}
        </Row>

        {/* No Results Message */}
        {filteredCourses.length === 0 && (
          <div className="no-results text-center py-5">
            <div className="no-results-icon mb-4">
              <FaSearch size={60} className="text-muted" />
            </div>
            <h4 className="mb-3">No Courses Found</h4>
            <p className="text-muted mb-4">Try a different search term or filter</p>
            <Button 
              variant="outline-primary" 
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
              }}
            >
              Show All Courses
            </Button>
          </div>
        )}

        {/* Admission Process Section */}
        <Row className="mt-5 pt-5">
          <Col lg={12}>
            <div className="section-header text-center mb-5">
              <Badge bg="light" text="dark" className="mb-3 px-3 py-2">
                <FaInfoCircle className="me-2" /> Admission Process
              </Badge>
              <h2 className="mb-3">Simple Admission Steps</h2>
              <p className="text-muted">Follow these easy steps to join our programs</p>
            </div>
            
            <div className="process-steps">
              <Row className="g-4">
                <Col lg={3} md={6}>
                  <div className="step-card">
                    <div className="step-number">1</div>
                    <div className="step-icon">
                      <FaFileAlt />
                    </div>
                    <h5>Check Eligibility</h5>
                    <p className="text-muted small">Review course-specific eligibility criteria</p>
                  </div>
                </Col>
                <Col lg={3} md={6}>
                  <div className="step-card">
                    <div className="step-number">2</div>
                    <div className="step-icon">
                      <FaPenAlt />
                    </div>
                    <h5>Apply </h5>
                    <p className="text-muted small">Submit online application form</p>
                  </div>
                </Col>
                <Col lg={3} md={6}>
                  <div className="step-card">
                    <div className="step-number">3</div>
                    <div className="step-icon">
                      <FaCheckCircle />
                    </div>
                    <h5>Document Verification</h5>
                    <p className="text-muted small">Submit required documents for verification</p>
                  </div>
                </Col>
                <Col lg={3} md={6}>
                  <div className="step-card">
                    <div className="step-number">4</div>
                    <div className="step-icon">
                      <FaUserGraduate />
                    </div>
                    <h5>Join Program</h5>
                    <p className="text-muted small">Complete admission and start learning</p>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Course Detail Modal */}
      {selectedCourse && (
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          centered 
          size="lg"
          className="course-detail-modal"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title>
              <div className="d-flex align-items-center">
                <div 
                  className="modal-course-icon me-3"
                  style={{ background: getCourseColor(selectedCourse.courseType) }}
                >
                  {getCourseIcon(selectedCourse.name, selectedCourse.description)}
                </div>
                <div>
                  <h3 className="mb-0">{selectedCourse.name}</h3>
                  <code className="text-muted small">{selectedCourse.code}</code>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Row className="mb-4">
              <Col lg={8}>
                <div className="course-info">
                  <h5 className="mb-3 text-primary">
                    <FaInfoCircle className="me-2" />
                    Course Overview
                  </h5>
                  <div className="course-description-full">
                    {selectedCourse.description ? (
                      selectedCourse.description.split('\n').map((paragraph, index) => (
                        paragraph.trim() && (
                          <p key={index} className="mb-3">
                            {paragraph.trim()}
                          </p>
                        )
                      ))
                    ) : (
                      <p className="text-muted">No description available.</p>
                    )}
                  </div>
                </div>
              </Col>
              <Col lg={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h6 className="text-primary mb-3">
                      <FaCalendarAlt className="me-2" />
                      Course Details
                    </h6>
                    <ListGroup variant="flush">
                      <ListGroup.Item className="d-flex justify-content-between py-2">
                        <span className="text-muted">Duration:</span>
                        <span className="fw-bold">{selectedCourse.duration}</span>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between py-2">
                        <span className="text-muted">Course Type:</span>
                        <span>{courseTypes[selectedCourse.courseType]}</span>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between py-2">
                        <span className="text-muted">Fee:</span>
                        <span className="fw-bold text-success">{formatCurrency(selectedCourse.fee)}</span>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between py-2">
                        <span className="text-muted">Available Seats:</span>
                        <Badge 
                          bg={selectedCourse.isSeatAvailable ? 
                            (selectedCourse.seatStatus === 'good' ? 'success' : 
                             selectedCourse.seatStatus === 'limited' ? 'warning' : 'danger') : 'secondary'}
                        >
                          {selectedCourse.isSeatAvailable ? `${selectedCourse.intake_seats} Seats` : 'Admission Closed'}
                        </Badge>
                      </ListGroup.Item>
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4">
              {selectedCourse.basicSubjects && (
                <Col lg={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <h5 className="text-primary mb-3">
                        <FaBook className="me-2" />
                        Basic Subjects
                      </h5>
                      <p className="text-muted">{selectedCourse.basicSubjects}</p>
                    </Card.Body>
                  </Card>
                </Col>
              )}
              
              {selectedCourse.optionalSubjects && selectedCourse.optionalSubjects.length > 0 && (
                <Col lg={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <h5 className="text-primary mb-3">
                        <FaListAlt className="me-2" />
                        Optional Subjects
                      </h5>
                      <ListGroup variant="flush">
                        {selectedCourse.optionalSubjects.map((subject, index) => (
                          <ListGroup.Item key={index} className="d-flex align-items-start py-2">
                            <div className="me-2">
                              <FaCheckCircle className="text-success mt-1" />
                            </div>
                            <span>{subject}</span>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>

            {selectedCourse.eligibility && (
              <Card className="border-0 shadow-sm mt-4">
                <Card.Body>
                  <h5 className="text-primary mb-3">
                    <FaCheckCircle className="me-2" />
                    Eligibility Criteria
                  </h5>
                  <div className="eligibility-details">
                    {selectedCourse.eligibility.split('\n').map((line, index) => (
                      line.trim() && (
                        <p key={index} className="mb-2 text-muted">
                          {line.trim()}
                        </p>
                      )
                    ))}
                  </div>
                </Card.Body>
              </Card>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={handleCloseModal}>
              Close
            </Button>
            {/* {selectedCourse.isSeatAvailable && (
              <Button variant="success">
                <FaPenAlt className="me-2" />
                Apply Now
              </Button>
            )} */}
          </Modal.Footer>
        </Modal>
      )}

      <style>{`
        .courses-portal {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
        }
        
        .courses-hero {
          background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
          position: relative;
          overflow: hidden;
        }
        
        .courses-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
          opacity: 0.3;
        }
        
        .hero-badge {
          font-size: 0.9rem;
          padding: 8px 16px;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .text-gradient {
          background: linear-gradient(90deg, #3498db, #2ecc71);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hero-stats {
          display: flex;
          gap: 30px;
          margin-top: 30px;
        }
        
        .stat-item {
          display: flex;
          flex-direction: column;
        }
        
        .hero-visual {
          position: relative;
          height: 300px;
        }
        
        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          animation: float 6s ease-in-out infinite;
        }
        
        .shape-1 {
          width: 100px;
          height: 100px;
          top: 20px;
          right: 50px;
          animation-delay: 0s;
        }
        
        .shape-2 {
          width: 70px;
          height: 70px;
          bottom: 40px;
          right: 100px;
          animation-delay: 2s;
        }
        
        .shape-3 {
          width: 50px;
          height: 50px;
          top: 100px;
          right: 150px;
          animation-delay: 4s;
        }
        
        .courses-preview-card {
          position: absolute;
          top: 50%;
          left: 0;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.95);
          padding: 20px;
          border-radius: 15px;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
          width: 200px;
        }
        
        .preview-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #3498db, #2ecc71);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          margin-bottom: 15px;
        }
        
        .search-filter-section {
          background: white;
          padding: 25px;
          border-radius: 15px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
        }
        
        .search-wrapper {
          position: relative;
        }
        
        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .search-icon {
          position: absolute;
          left: 20px;
          color: #6c757d;
          z-index: 2;
        }
        
        .search-input {
          width: 100%;
          padding: 15px 20px 15px 50px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
          padding-right: 100px;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 5px 15px rgba(52, 152, 219, 0.1);
        }
        
        .search-clear {
          position: absolute;
          right: 10px;
          padding: 5px 10px;
          font-size: 0.875rem;
          text-decoration: none;
        }
        
        .filter-badges {
          display: flex;
          flex-wrap: wrap;
        }
        
        .filter-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }
        
        .filter-badge:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }
        
        .course-card-wrapper {
          position: relative;
          height: 100%;
        }
        
        .course-card {
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.4s ease;
          height: 100%;
        }
        
        .course-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
        }
        
        .course-card-header {
          height: 120px;
          position: relative;
          padding: 20px;
        }
        
        .course-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .course-icon {
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          backdrop-filter: blur(10px);
        }
        
        .course-type-badge {
          font-size: 0.75rem;
          padding: 6px 12px;
          border-radius: 20px;
          text-transform: uppercase;
          font-weight: 600;
        }
        
        .course-title {
          font-weight: 600;
          color: #2c3e50;
        }
        
        .course-description {
          line-height: 1.6;
          min-height: 60px;
          font-size: 0.95rem;
        }
        
        .course-subjects {
          min-height: 60px;
        }
        
        .course-details {
          min-height: 80px;
        }
        
        .course-stats {
          display: flex;
          align-items: center;
        }
        
        .stat {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .stat-icon {
          font-size: 16px;
          color: #3498db;
        }
        
        .stat-label {
          font-size: 0.8rem;
          color: #6c757d;
        }
        
        .course-action-btn {
          padding: 8px 20px;
          border-radius: 8px;
          font-weight: 500;
          display: flex;
          align-items: center;
        }
        
        .loading-animation {
          display: flex;
          justify-content: center;
        }
        
        .loading-dots {
          display: flex;
          gap: 10px;
        }
        
        .dot {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3498db, #2ecc71);
          animation: bounce 1.4s infinite ease-in-out;
        }
        
        .dot:nth-child(1) { animation-delay: -0.32s; }
        .dot:nth-child(2) { animation-delay: -0.16s; }
        
        .process-steps {
          padding: 0 20px;
        }
        
        .step-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          height: 100%;
          position: relative;
        }
        
        .step-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
        }
        
        .step-number {
          position: absolute;
          top: -15px;
          left: -15px;
          width: 40px;
          height: 40px;
          background: #3498db;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 1.2rem;
        }
        
        .step-icon {
          width: 70px;
          height: 70px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, #3498db, #2ecc71);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
        }
        
        .modal-course-icon {
          width: 60px;
          height: 60px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
        }
        
        .course-description-full {
          line-height: 1.8;
          color: #333;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        
        @media (max-width: 768px) {
          .hero-stats {
            flex-direction: column;
            gap: 15px;
          }
          
          .search-filter-section {
            padding: 15px;
          }
          
          .step-card {
            padding: 20px;
          }
          
          .modal-course-icon {
            width: 50px;
            height: 50px;
            font-size: 24px;
          }
        }
        
        @media (max-width: 576px) {
          .hero-visual {
            height: 200px;
          }
          
          .courses-preview-card {
            width: 150px;
            padding: 15px;
          }
          
          .filter-badge {
            padding: 6px 12px;
            font-size: 0.8rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Courses;